import React, { useState, useEffect, useContext } from 'react';
import { 
  doc, getDoc, updateDoc, arrayUnion, arrayRemove, runTransaction, writeBatch, setDoc, query, collection, where, getDocs
} from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faUpload, faFilePdf, faFileImage, faSpinner,
  faChevronUp, faChevronDown, faQrcode
} from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import QRCode from "react-qr-code";
import styles from './AddEquipmentModal.module.css';

const AddAudioEquipmentModal = ({ isOpen, onClose, onSave, editItem }) => {
  const { userData } = useContext(UserContext);
  
  // Basic form state
  const [type, setType] = useState('');
  const [subType, setSubType] = useState('');
  const [brand, setBrand] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [rentPerDay, setRentPerDay] = useState('');
  
  // Individual items handling
  const [individualItems, setIndividualItems] = useState([]);
  const [showIndividualItemsPanel, setShowIndividualItemsPanel] = useState(false);
  const [copyInfoToAllItems, setCopyInfoToAllItems] = useState(false);
  
  // File upload state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentName, setAttachmentName] = useState('');
  
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  
  // Constants
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const APP_URL = 'https://ontour.co.in'; // Base URL for QR codes
  
  // Audio equipment options
  const typeOptions = [
    'Microphones',
    'Mixing Consoles',
    'Speakers',
    'Amplifiers',
    'Signal Processors',
    'Direct Boxes (DI Boxes)',
    'Cables and Connectors',
    'Stands and Mounts',
    'In-Ear Monitors (IEM)',
    'Headphones',
    'Playback and Recording Devices',
    'Stage and Studio Accessories',
    'Wireless Systems',
    'Power Management',
    'Monitoring and Analysis',
    'Miscellaneous',
  ];
  
  const subTypeOptions = {
    'Microphones': ['Dynamic Microphones', 'Condenser Microphones', 'Ribbon Microphones', 'Wireless Microphones', 'Lavalier Microphones', 'Boundary Microphones', 'Shotgun Microphones', 'Instrument Microphones'],
    'Mixing Consoles': ['Analog', 'Digital', 'Hybrid'],
    'Speakers': ['PA Speakers', 'Studio Monitors', 'Subwoofers', 'Active (Powered) Speakers', 'Passive Speakers', 'Line Array Speakers', 'Stage Monitors (Floor Monitors)', 'Full-Range Speakers'],
    'Amplifiers': ['Power Amplifiers', 'Integrated Amplifiers', 'Headphone Amplifiers'],
    'Signal Processors': ['Equalizers', 'Compressors', 'Limiters', 'Gates', 'Reverb Units', 'Delay Units', 'Multi-Effects Processors'],
    'Direct Boxes (DI Boxes)': ['Passive DI Boxes', 'Active DI Boxes'],
    'Cables and Connectors': ['Microphone Cables (XLR)', 'Instrument Cables (TS)', 'Speaker Cables (Speakon, Banana, etc.)', 'Patch Cables', 'Multi-Pin Connectors', 'Adapters and Converters'],
    'Stands and Mounts': ['Microphone Stands', 'Speaker Stands', 'Monitor Mounts', 'Rack Mounts'],
    'In-Ear Monitors (IEM)': ['Wired IEM Systems', 'Wireless IEM Systems'],
    'Headphones': ['Closed-Back Headphones', 'Open-Back Headphones', 'In-Ear Headphones'],
    'Playback and Recording Devices': ['Multitrack Recorders', 'Field Recorders', 'Playback Devices (CD Players, Media Players)', 'Audio Interfaces'],
    'Stage and Studio Accessories': ['Pop Filters', 'Shock Mounts', 'Isolation Shields', 'Acoustic Panels', 'Cable Management Systems'],
    'Wireless Systems': ['Wireless Microphone Systems', 'Wireless Instrument Systems', 'Wireless IEM Systems'],
    'Power Management': ['Power Conditioners', 'Surge Protectors', 'Uninterruptible Power Supplies (UPS)'],
    'Monitoring and Analysis': ['Spectrum Analyzers', 'SPL Meters', 'Feedback Suppressors'],
    'Miscellaneous': ['Drum Shields', 'Microphone Windscreens', 'Snake Cables', 'Cable Reels', 'Patch Bays'],
  };
  
  const brandOptions = {
    'Microphones': ['Shure', 'Sennheiser', 'Audio-Technica', 'AKG', 'Ahuja', 'Neumann', 'Rode', 'Electro-Voice', 'Beyerdynamic', 'DPA', 'Blue', 'Samson', 'Heil Sound', 'Audix', 'Lewitt', 'Telefunken', 'SE Electronics', 'Earthworks', 'Miktek', 'MXL', 'CAD Audio', 'Other'],
    'Mixing Consoles': ['Yamaha', 'Allen & Heath', 'Midas', 'Behringer', 'Soundcraft', 'Avid', 'Mackie', 'PreSonus', 'SSL (Solid State Logic)', 'DiGiCo', 'QSC', 'Roland', 'Tascam', 'Zoom', 'MOTU', 'Alesis', 'Other'],
    'Speakers': ['JBL', 'QSC', 'Electro-Voice', 'Meyer Sound', 'RCF', 'Yamaha', 'd&b audiotechnik', 'L-Acoustics', 'Bose', 'Martin Audio', 'Funktion-One', 'Turbosound', 'Genelec', 'KRK', 'Adam Audio', 'Presonus', 'Mackie', 'Behringer', 'Dynaudio', 'Neumann', 'Other'],
    'Amplifiers': ['Crown', 'QSC', 'Lab Gruppen', 'Yamaha', 'Peavey', 'Behringer', 'Electro-Voice', 'Powersoft', 'Ashly', 'ART', 'Crest Audio', 'Dynacord', 'Mackie', 'Ampeg', 'Kustom', 'Line 6', 'Marshall', 'Orange', 'Randall', 'Rivera', 'Other'],
    'Signal Processors': ['DBX', 'BSS', 'Lexicon', 'TC Electronic', 'Klark Teknik', 'Waves', 'Behringer', 'Yamaha', 'ART', 'Alesis', 'Other'],
    'Direct Boxes (DI Boxes)': ['Radial Engineering', 'Whirlwind', 'Countryman', 'Behringer', 'ART', 'ProCo', 'RapcoHorizon', 'BSS', 'Other'],
    'Cables and Connectors': ['Mogami', 'Monster', 'Hosa', 'ProCo', 'Canare', 'Neutrik', 'Switchcraft', 'Whirlwind', 'Planet Waves', 'Other'],
    'Stands and Mounts': ['K&M (König & Meyer)', 'On-Stage Stands', 'Hercules', 'Ultimate Support', 'Atlas Sound', 'Gator Frameworks', 'Samson', 'Other'],
    'In-Ear Monitors (IEM)': ['Shure', 'Sennheiser', 'Ultimate Ears', 'Westone', 'Audio-Technica', 'MEE Audio', 'Behringer', 'AKG', 'Other'],
    'Headphones': ['Sennheiser', 'Audio-Technica', 'Beyerdynamic', 'Shure', 'Sony', 'AKG', 'Focal', 'Pioneer', 'Philips', 'Other'],
    'Playback and Recording Devices': ['TASCAM', 'Zoom', 'Roland', 'PreSonus', 'Alesis', 'Sound Devices', 'Behringer', 'Yamaha', 'MOTU', 'Other'],
    'Stage and Studio Accessories': ['Auralex', 'Primacoustic', 'Vicoustic', 'Gator Cases', 'SKB', 'Road Ready', 'Odyssey', 'Gruv Gear', 'Other'],
    'Wireless Systems': ['Shure', 'Sennheiser', 'Audio-Technica', 'AKG', 'Electro-Voice', 'Line 6', 'Sony', 'Xvive', 'Samson', 'Other'],
    'Power Management': ['Furman', 'Tripp Lite', 'Panamax', 'ART', 'Monster', 'APC', 'Middle Atlantic', 'Other'],
    'Monitoring and Analysis': ['Behringer', 'RTA', 'NTI Audio', 'DBX', 'TC Electronic', 'Yamaha', 'ART', 'Other'],
    'Miscellaneous': ['Hosa', 'ProCo', 'Radial Engineering', 'Gator Cases', 'SKB', 'On-Stage Stands', 'K&M (König & Meyer)', 'Ultimate Support', 'Other'],
  };
  
  // Initialize form with edit item data
useEffect(() => {
  // Initialize form values
  if (editItem) {
    setType(editItem.type || '');
    setSubType(editItem.subType || '');
    setBrand(editItem.brand || '');
    setName(editItem.name || '');
    setDescription(editItem.description || '');
    setQuantity(editItem.totalQuantity?.toString() || '');
    setRentPerDay(editItem.rentPerDay?.toString() || '');
    
    // Set photo preview if exists - using coverPhotoUrl from the new structure
    if (editItem.coverPhotoUrl) {
      setPhotoPreview(editItem.coverPhotoUrl);
    }
    
    // Set attachment name if exists
    if (editItem.fileUrl) {
      // Extract filename from URL
      const filename = editItem.fileUrl.substring(editItem.fileUrl.lastIndexOf('/') + 1);
      setAttachmentName(filename);
    }
    
    // Initialize individual items if this is an existing group with items
    if (editItem.groupId) {
      fetchIndividualItems(editItem.groupId);
    }
    
    // Always show individual items panel
    setShowIndividualItemsPanel(true);
  } else {
    // Reset all form fields when opening for a new item
    setType('');
    setSubType('');
    setBrand('');
    setName('');
    setDescription('');
    setQuantity('');
    setRentPerDay('');
    setPhotoFile(null);
    setPhotoPreview('');
    setAttachmentFile(null);
    setAttachmentName('');
    setIndividualItems([]);
    setShowIndividualItemsPanel(false);
    setFormErrors({});
  }
}, [editItem]);
  
  // Function to fetch individual items if editing
  const fetchIndividualItems = async (groupId) => {
    if (!userData?.userId || !groupId) return;
    
    try {
      const db = getFirestoreInstance();
      const itemsQuery = query(
        collection(db, 'users', userData.userId, 'inventoryItems'),
        where('groupId', '==', groupId)
      );
      
      const itemsSnap = await getDocs(itemsQuery);
      
      if (!itemsSnap.empty) {
        const items = itemsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // For UI interaction, need a local photoPreview
          photoPreview: doc.data().photoUrl
        }));
        
        setIndividualItems(items);
      }
    } catch (error) {
      console.error('Error fetching individual items:', error);
    }
  };
  
  // Generate individual items based on quantity
  const generateIndividualItems = () => {
    if (parseInt(quantity) <= 0 || !name) return;
    
    // Keep existing items if editing
    const existingItems = [...individualItems];
    const existingCount = existingItems.length;
    const targetCount = parseInt(quantity);
    
    // If we need to add more items
    if (targetCount > existingCount) {
      const newItems = [];
      
      for (let i = existingCount; i < targetCount; i++) {
        // Create a serial number based on name (e.g., "Shure SM58-001")
        const paddedIndex = String(i + 1).padStart(3, '0');
        const serialNumber = `${name.replace(/\s+/g, '-')}-${paddedIndex}`;
        
        newItems.push({
          id: `item_${Date.now()}_${i}`, // Temporary ID for the form
          serialNumber,
          photoUrl: '', // Each item can have its own photo
          photoPreview: '', // For UI
          photoFile: null, // For upload handling
          condition: 'Good',
          status: 'Available',
          maintenanceNotes: '',
          yearOfPurchase: '',
          purchasePrice: '',
          warrantyExpiryDate: '',
          location: ''
        });
      }
      
      setIndividualItems([...existingItems, ...newItems]);
    } else if (targetCount < existingCount) {
      // If we need to remove items, keep just the first 'targetCount' items
      setIndividualItems(existingItems.slice(0, targetCount));
    }
  };
  
  // Watch for quantity changes to update individual items
  useEffect(() => {
    if (parseInt(quantity) > 0) {
      generateIndividualItems();
    }
  }, [quantity]);

  // Copy info from first item to all others when toggle is enabled
  useEffect(() => {
    if (copyInfoToAllItems && individualItems.length > 1) {
      const firstItem = individualItems[0];
      const updatedItems = individualItems.map((item, index) => {
        if (index === 0) return item; // Don't modify the first item
        
        return {
          ...item,
          condition: firstItem.condition,
          maintenanceNotes: firstItem.maintenanceNotes,
          yearOfPurchase: firstItem.yearOfPurchase,
          purchasePrice: firstItem.purchasePrice,
          warrantyExpiryDate: firstItem.warrantyExpiryDate,
          location: firstItem.location,
          model: firstItem.model,
          // Keep the original serial number and status
          serialNumber: item.serialNumber,
          status: item.status
        };
      });
      
      setIndividualItems(updatedItems);
    }
  }, [copyInfoToAllItems]);
  
  // Handle file selection for image
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setFormErrors({
        ...formErrors,
        photo: `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`
      });
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setFormErrors({
        ...formErrors,
        photo: 'Invalid file type. Please upload an image.'
      });
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    setPhotoFile(file);
    setFormErrors({ ...formErrors, photo: null });
  };
  
  // Handle file selection for individual item
  const handleIndividualPhotoChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setFormErrors({
        ...formErrors,
        [`itemPhoto_${index}`]: `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`
      });
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setFormErrors({
        ...formErrors,
        [`itemPhoto_${index}`]: 'Invalid file type. Please upload an image.'
      });
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      const updatedItems = [...individualItems];
      updatedItems[index].photoPreview = reader.result;
      updatedItems[index].photoFile = file;
      setIndividualItems(updatedItems);
    };
    reader.readAsDataURL(file);
    
    // Clear error
    setFormErrors({
      ...formErrors,
      [`itemPhoto_${index}`]: null
    });
  };
  
  // Handle file selection for attachment
  const handleAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setFormErrors({
        ...formErrors,
        attachment: `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`
      });
      return;
    }
    
    // Validate file type (PDF only)
    if (file.type !== 'application/pdf') {
      setFormErrors({
        ...formErrors,
        attachment: 'Invalid file type. Please upload a PDF.'
      });
      return;
    }
    
    setAttachmentFile(file);
    setAttachmentName(file.name);
    setFormErrors({ ...formErrors, attachment: null });
  };

  // Handle individual item field change
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...individualItems];
    updatedItems[index][field] = value;
    setIndividualItems(updatedItems);
  };
  
  // Generate keywords for search
  const generateKeywords = () => {
    if (!name && !brand && !type) return [];
    
    const keywordSet = new Set();
    
    // Extract base model name (without serial number suffix)
    let baseName = name;
    // If name contains something like "SM58-01", extract just "SM58"
    const baseNameMatch = name ? name.match(/^(.+?)(?:-\d+)?$/) : null;
    if (baseNameMatch && baseNameMatch[1]) {
      baseName = baseNameMatch[1].trim();
    }
    
    // Add brand combinations
    if (brand) {
      const brandLower = brand.toLowerCase();
      keywordSet.add(brandLower);
      
      // Brand + equipment type combinations
      if (type) {
        const typeLower = type.toLowerCase();
        keywordSet.add(`${brandLower} ${typeLower}`);
        
        // Add "brand mic" for microphones
        if (type === 'Microphones') {
          keywordSet.add(`${brandLower} mic`);
          keywordSet.add(`${brandLower} mics`);
        }
      }
      
      // Brand + model (both with and without spaces)
      if (individualItems.length > 0 && individualItems[0].model) {
        const modelLower = individualItems[0].model.toLowerCase();
        keywordSet.add(`${brandLower} ${modelLower}`);
        keywordSet.add(`${brandLower}${modelLower}`);
        
        // Handle letter-number spacing variations
        if (modelLower.match(/[a-z][0-9]|[0-9][a-z]/i)) {
          const spacedModel = modelLower
            .replace(/([a-z])(\d)/gi, '$1 $2')
            .replace(/(\d)([a-z])/gi, '$1 $2');
          keywordSet.add(`${brandLower} ${spacedModel}`);
        }
      }
      
      // Brand + base name (without serial number)
      if (baseName && baseName !== brand) {
        const baseNameLower = baseName.toLowerCase();
        keywordSet.add(`${brandLower} ${baseNameLower}`);
        
        // If base name has model number pattern like "SM58", add variant with space
        if (baseNameLower.match(/[a-z][0-9]|[0-9][a-z]/i)) {
          const spacedBaseName = baseNameLower
            .replace(/([a-z])(\d)/gi, '$1 $2')
            .replace(/(\d)([a-z])/gi, '$1 $2');
          keywordSet.add(`${brandLower} ${spacedBaseName}`);
        }
      }
    }
    
    // Add base name without serial suffix
    if (baseName) {
      const baseNameLower = baseName.toLowerCase();
      keywordSet.add(baseNameLower);
      
      // If it has a model number pattern like "SM58", add variant with space
      if (baseNameLower.match(/[a-z][0-9]|[0-9][a-z]/i)) {
        keywordSet.add(baseNameLower.replace(/([a-z])(\d)/gi, '$1 $2'));
        keywordSet.add(baseNameLower.replace(/(\d)([a-z])/gi, '$1 $2'));
      }
    }
    
    // Add individual words from name
    if (baseName) {
      const words = baseName.toLowerCase().split(/\s+/);
      words.forEach(word => {
        if (word.length > 2) keywordSet.add(word);
      });
    }
    
    // Add the full equipment name
    if (name) {
      keywordSet.add(name.toLowerCase());
    }
    
    // Add model variations from first item
    if (individualItems.length > 0 && individualItems[0].model) {
      const modelLower = individualItems[0].model.toLowerCase();
      keywordSet.add(modelLower);
      
      // Add spacing variations for model numbers
      if (modelLower.match(/[a-z][0-9]|[0-9][a-z]/i)) {
        keywordSet.add(modelLower.replace(/([a-z])(\d)/gi, '$1 $2'));
        keywordSet.add(modelLower.replace(/(\d)([a-z])/gi, '$1 $2'));
      }
    }
    
    // Type and subtype keywords
    if (type) {
      const typeLower = type.toLowerCase();
      keywordSet.add(typeLower);
      
      // Add common abbreviations based on type
      if (type === 'Microphones') {
        keywordSet.add('mic');
        keywordSet.add('mics');
        keywordSet.add('microphone');
      } else if (type === 'Amplifiers') {
        keywordSet.add('amp');
        keywordSet.add('amps');
      } else if (type === 'Mixing Consoles') {
        keywordSet.add('mixer');
        keywordSet.add('console');
      }
    }
    
    if (subType) {
      keywordSet.add(subType.toLowerCase());
    }
    
    // Convert to array and filter out any very short terms
    return Array.from(keywordSet).filter(keyword => 
      keyword && keyword.length > 1
    );
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    let errors = {};
    if (!name) errors.name = 'Name is required';
    if (!type) errors.type = 'Type is required';
    if (!subType) errors.subType = 'Sub Type is required';
    if (!brand) errors.brand = 'Brand is required';
    if (!quantity) errors.quantity = 'Quantity is required';
    if (!rentPerDay) errors.rentPerDay = 'Rent per day is required';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const db = getFirestoreInstance();
      const timestamp = new Date().toISOString();
      
      // Generate or update group ID
      const groupId = editItem && editItem.groupId 
        ? editItem.groupId 
        : `${type.replace(/\s+/g, '_').toLowerCase()}_${brand.replace(/\s+/g, '_').toLowerCase()}_${individualItems.length > 0 && individualItems[0].model ? individualItems[0].model.replace(/\s+/g, '_').toLowerCase() : ''}_${Date.now()}`;
      
      // Upload main cover photo
      let coverPhotoUrl = editItem ? editItem.coverPhotoUrl : null;
      if (photoFile) {
        // Delete previous photo if exists
        if (editItem && editItem.coverPhotoUrl) {
          try {
            // Extract the file path from the URL and delete from Backblaze
            const photoPath = extractFilePathFromUrl(editItem.coverPhotoUrl);
            if (photoPath) {
              await deleteFileFromBackblaze(photoPath);
              console.log('Previous photo deleted successfully');
            }
          } catch (deleteError) {
            console.error('Error deleting previous photo:', deleteError);
            // Continue with upload even if delete fails
          }
        }
        
        // Upload new photo
        coverPhotoUrl = await uploadMediaToBackblaze(photoFile, 'photo', groupId);
      }
      
      // Upload attachment file
      let fileUrl = editItem ? editItem.fileUrl : null;
      if (attachmentFile) {
        // Delete previous attachment if exists
        if (editItem && editItem.fileUrl) {
          try {
            // Extract the file path from the URL and delete from Backblaze
            const filePath = extractFilePathFromUrl(editItem.fileUrl);
            if (filePath) {
              await deleteFileFromBackblaze(filePath);
              console.log('Previous attachment deleted successfully');
            }
          } catch (deleteError) {
            console.error('Error deleting previous attachment:', deleteError);
            // Continue with upload even if delete fails
          }
        }
        
        // Upload new attachment
        fileUrl = await uploadMediaToBackblaze(attachmentFile, 'attachment', groupId);
      }
      
      // Generate keywords for search
      const generatedKeywords = generateKeywords();
      
      // Create the group document
      const groupData = {
        groupId,
        vendorId: userData.userId,
        inventoryType: 'Audio Equipment',
        type,
        subType,
        brand,
        model: individualItems.length > 0 ? individualItems[0].model || '' : '',
        name,
        description: description || '',
        rentPerDay: parseFloat(rentPerDay) || 0,
        coverPhotoUrl,
        fileUrl,
        totalQuantity: parseInt(quantity) || 0,
        availableQuantity: parseInt(quantity) || 0,
        reservedQuantity: 0,
        damagedQuantity: 0,
        maintenanceQuantity: 0,
        keywords: generatedKeywords,
        createdAt: editItem && editItem.createdAt ? editItem.createdAt : timestamp,
        updatedAt: timestamp,
      };
      
      // Store in inventoryGroups subcollection
      const groupRef = doc(db, 'users', userData.userId, 'inventoryGroups', groupId);
      await setDoc(groupRef, groupData);
      
      // Create batch for efficient writes
      const batch = writeBatch(db);
      
      // When editing, first remove all existing individual items
      if (editItem && editItem.groupId) {
        const existingItemsQuery = query(
          collection(db, 'users', userData.userId, 'inventoryItems'),
          where('groupId', '==', editItem.groupId)
        );
        
        const existingItemsSnap = await getDocs(existingItemsQuery);
        existingItemsSnap.forEach(doc => {
          batch.delete(doc.ref);
        });
      }
      
      // Process each individual item
      for (const item of individualItems) {
        const uniqueId = groupId.split('_').pop() || Date.now().toString();
        const itemId = `${type.replace(/\s+/g, '_').toLowerCase()}_${brand.replace(/\s+/g, '_').toLowerCase()}_${uniqueId}_${item.serialNumber.replace(/\s+/g, '_').toLowerCase()}`;


        // Generate QR code URL - this will be a URL to the scan endpoint
        const qrCodeUrl = `${APP_URL}/scan/assigned-inventory/${itemId}`;
        
        // Upload individual item photo if present
        let itemPhotoUrl = item.photoUrl || null;
        if (item.photoFile) {
          itemPhotoUrl = await uploadMediaToBackblaze(item.photoFile, 'photo', itemId);
        }
        
        const itemData = {
          itemId,
          groupId,
          vendorId: userData.userId,
          allowedUserIds: [], // Initialize with empty array for authorized users
          inventoryType: 'Audio Equipment',
          type,
          subType,
          brand,
          model: item.model || '',
          name,
          serialNumber: item.serialNumber,
          rentPerDay: parseFloat(rentPerDay) || 0,
          photoUrl: itemPhotoUrl,
          condition: item.condition || 'Good',
          status: item.status || 'Available',
          yearOfPurchase: item.yearOfPurchase ? parseInt(item.yearOfPurchase) : null,
          purchasePrice: item.purchasePrice ? parseFloat(item.purchasePrice) : null,
          warrantyExpiryDate: item.warrantyExpiryDate || null,
          location: item.location || '',
          maintenanceNotes: item.maintenanceNotes || '',
          assignedTourIds: item.assignedTourIds || {},
          qrCodeUrl: qrCodeUrl, // Store QR code URL for easy reference
          lastUsed: item.lastUsed || null,
          createdAt: item.createdAt || timestamp,
          updatedAt: timestamp,
        };
        
        const itemRef = doc(db, 'users', userData.userId, 'inventoryItems', itemId);
        batch.set(itemRef, itemData);
      }
      
      await batch.commit();
      
      // Update UI and close modal
      onSave({...groupData, id: groupId}, 'audioEquipment');
      
    } catch (error) {
      console.error('Error saving audio equipment:', error);
      setFormErrors({
        ...formErrors,
        submit: `Failed to ${editItem ? 'update' : 'save'} equipment. Error: ${error.message}`
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Upload file to Backblaze through API
  const uploadMediaToBackblaze = async (file, type, itemId) => {
    try {
      setUploadProgress(0);
      console.log(`Uploading ${type} for item ${itemId}`);
      
      // Create FormData
      const formData = new FormData();
      formData.append('media', file);
      
      // Explicitly set the type based on file mimetype
      const actualType = file.type.startsWith('image/') ? 'photo' : 'attachment';
      formData.append('type', actualType);
      
      // Include metadata
      const queryParams = new URLSearchParams({
        context: 'inventory',
        category: 'audioEquipment',
        itemId: itemId
      }).toString();
      
      // Get auth token from UserContext or localStorage
      const authToken = localStorage.getItem('authToken');
      
      // Make the request to upload endpoint
      const response = await fetch(`/api/processAndUploadMedia?${queryParams}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }
      
      const responseData = await response.json();
      console.log('Upload response:', responseData);
      
      // Extract URL carefully with fallbacks
      let url = null;
      
      // Check all possible locations for the URL
      if (responseData.files && responseData.files.length > 0) {
        url = responseData.files[0].url;
      } else if (responseData.url) {
        url = responseData.url;
      } else if (responseData.filePath) {
        // Construct URL from file path
        url = `https://Ontour-Media.s3.eu-central-003.backblazeb2.com/${responseData.filePath}`;
      }
      
      setUploadProgress(100);
      console.log(`Upload complete. URL: ${url || 'NULL'}`);
      
      if (!url && responseData.success) {
        console.error('Upload was successful but no URL was returned');
      }
      
      return url;
    } catch (error) {
      console.error('Error uploading to Backblaze:', error);
      setUploadProgress(0);
      throw error;
    }
  };
  
  if (!isOpen) return null;

  // Helper function to extract file path from Backblaze URL
  const extractFilePathFromUrl = (url) => {
    if (!url) return null;
    
    try {
      // Example URL format: https://Ontour-Media.s3.eu-central-003.backblazeb2.com/inventory/user123/audioEquipment/item456/photo.jpg
      const regex = /https:\/\/[^\/]+\.s3\.[^\/]+\.backblazeb2\.com\/(.+)/;
      const match = url.match(regex);
      
      if (match && match[1]) {
        return decodeURIComponent(match[1]);
      }
      return null;
    } catch (error) {
      console.error('Error extracting file path from URL:', error);
      return null;
    }
  };

  // Function to delete a file from Backblaze
  const deleteFileFromBackblaze = async (filePath) => {
    try {
      // Get auth token from localStorage
      const authToken = localStorage.getItem('authToken');
      
      // Send delete request to your backend API
      const response = await fetch('/api/deleteBackblazeFile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ filePath })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete file');
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting file from Backblaze:', error);
      throw error;
    }
  };
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        <h2 className={styles.modalTitle}>
          {editItem ? 'Edit Audio Equipment' : 'Add Audio Equipment'}
        </h2>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Photo Section */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Equipment Photo</h3>
            <div className={styles.photoUploadSection}>
              {photoPreview ? (
                <div className={styles.photoPreviewContainer}>
                  <img 
                    src={photoPreview} 
                    alt="Equipment preview" 
                    className={styles.photoPreview} 
                  />
                  <button 
                    type="button" 
                    className={styles.removePhotoButton}
                    onClick={() => {
                      setPhotoFile(null);
                      setPhotoPreview('');
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <label className={styles.photoUploadArea}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className={styles.fileInput}
                  />
                  <FontAwesomeIcon icon={faUpload} className={styles.uploadIcon} />
                  <div className={styles.uploadText}>
                    <p>Drop your image here or click to browse</p>
                    <span className={styles.uploadInfo}>
                      Max size: 5MB • Recommended: 1024×1024px
                    </span>
                  </div>
                </label>
              )}
              {formErrors.photo && (
                <p className={styles.errorText}>{formErrors.photo}</p>
              )}
            </div>
          </div>
          
          {/* Details Section */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Equipment Details</h3>
            
            <div className={styles.formGroup}>
              <label htmlFor="name" className={styles.formLabel}>Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`${styles.formInput} ${formErrors.name ? styles.inputError : ''}`}
                placeholder="Enter equipment name"
              />
              {formErrors.name && (
                <p className={styles.errorText}>{formErrors.name}</p>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="type" className={styles.formLabel}>Type</label>
              <select
                id="type"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setSubType('');
                  setBrand('');
                }}
                className={`${styles.formSelect} ${formErrors.type ? styles.inputError : ''}`}
              >
                <option value="">Select Type</option>
                {typeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {formErrors.type && (
                <p className={styles.errorText}>{formErrors.type}</p>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="subType" className={styles.formLabel}>Sub Type</label>
              <select
                id="subType"
                value={subType}
                onChange={(e) => setSubType(e.target.value)}
                className={`${styles.formSelect} ${formErrors.subType ? styles.inputError : ''}`}
                disabled={!type}
              >
                <option value="">Select Sub Type</option>
                {type && subTypeOptions[type]?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {formErrors.subType && (
                <p className={styles.errorText}>{formErrors.subType}</p>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="brand" className={styles.formLabel}>Brand</label>
              <select
                id="brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className={`${styles.formSelect} ${formErrors.brand ? styles.inputError : ''}`}
                disabled={!type}
              >
                <option value="">Select Brand</option>
                {type && brandOptions[type]?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {formErrors.brand && (
                <p className={styles.errorText}>{formErrors.brand}</p>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="description" className={styles.formLabel}>Description</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={styles.formTextarea}
                placeholder="Enter equipment description"
                maxLength={1000}
              />
              <p className={styles.charCount}>
                {description.length}/1000 characters
              </p>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="quantity" className={styles.formLabel}>Quantity</label>
                <input
                  type="number"
                  id="quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className={`${styles.formInput} ${formErrors.quantity ? styles.inputError : ''}`}
                  placeholder="Enter quantity"
                  min="1"
                  disabled={!name} // Disable quantity field if name is empty
                />
                {formErrors.quantity && (
                  <p className={styles.errorText}>{formErrors.quantity}</p>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="rentPerDay" className={styles.formLabel}>Rent per Day (₹)</label>
                <input
                  type="number"
                  id="rentPerDay"
                  value={rentPerDay}
                  onChange={(e) => setRentPerDay(e.target.value)}
                  className={`${styles.formInput} ${formErrors.rentPerDay ? styles.inputError : ''}`}
                  placeholder="Enter rent per day"
                  min="0"
                  step="0.01"
                />
                {formErrors.rentPerDay && (
                  <p className={styles.errorText}>{formErrors.rentPerDay}</p>
                )}
              </div>
            </div>
            
            {/* Copy info toggle - only shows if quantity > 1 */}
            {parseInt(quantity) > 1 && (
              <div className={styles.formGroup}>
                <div className={styles.toggleGroup}>
                  <label htmlFor="copyInfoToAllItems" className={styles.toggleLabel}>
                    Copy info to all items
                  </label>
                  <div className={styles.toggle}>
                    <input
                      type="checkbox"
                      id="copyInfoToAllItems"
                      checked={copyInfoToAllItems}
                      onChange={(e) => setCopyInfoToAllItems(e.target.checked)}
                      className={styles.toggleInput}
                    />
                    <label htmlFor="copyInfoToAllItems" className={styles.toggleSlider}></label>
                  </div>
                </div>
                <p className={styles.toggleHint}>
                  Enable to copy details from the first item to all other items (serial numbers will remain unique)
                </p>
              </div>
            )}
          </div>
          
          {/* Individual Items Section */}
          {parseInt(quantity) > 0 && (
            <div className={styles.individualItemsContainer}>
              <h3 className={styles.sectionTitle}>
                Individual Items
                <button 
                  type="button" 
                  className={styles.togglePanelButton} 
                  onClick={() => setShowIndividualItemsPanel(!showIndividualItemsPanel)}
                >
                  <FontAwesomeIcon icon={showIndividualItemsPanel ? faChevronUp : faChevronDown} />
                </button>
              </h3>
              
              {showIndividualItemsPanel && (
                <div className={styles.individualItemsList}>
                  {individualItems.map((item, index) => (
                    <div key={item.id} className={styles.individualItem}>
                      <h4 className={styles.itemHeader}>Item {index + 1}</h4>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor={`serialNumber_${index}`} className={styles.formLabel}>
                          Serial Number
                        </label>
                        <input
                          type="text"
                          id={`serialNumber_${index}`}
                          value={item.serialNumber}
                          onChange={(e) => handleItemChange(index, 'serialNumber', e.target.value)}
                          className={styles.formInput}
                        />
                      </div>
                  
                      
                      <div className={styles.formGroup}>
                        <label htmlFor={`condition_${index}`} className={styles.formLabel}>
                          Condition
                        </label>
                        <select
                          id={`condition_${index}`}
                          value={item.condition}
                          onChange={(e) => handleItemChange(index, 'condition', e.target.value)}
                          className={styles.formSelect}
                        >
                          <option value="Excellent">Excellent</option>
                          <option value="Good">Good</option>
                          <option value="Fair">Fair</option>
                          <option value="Poor">Poor</option>
                        </select>
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor={`status_${index}`} className={styles.formLabel}>
                          Status
                        </label>
                        <div className={styles.statusDisplay}>
                          {item.status || 'Available'}
                        </div>
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          QR Code
                        </label>
                        <div className={styles.qrCodePreview}>
                        <div style={{ background: 'white', padding: '16px', margin: '0 auto' }}>
  <QRCode
    size={256}
    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
    value={`${APP_URL}/scan/${item.id}`} // Use your actual variable here
    viewBox={`0 0 256 256`}
  />
</div>
                          <p><strong>{item.model || 'No Model'}</strong></p>
                          <p>For quick identification</p>
                        </div>
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor={`yearOfPurchase_${index}`} className={styles.formLabel}>
                          Year of Purchase
                        </label>
                        <input
                          type="number"
                          id={`yearOfPurchase_${index}`}
                          value={item.yearOfPurchase || ''}
                          onChange={(e) => handleItemChange(index, 'yearOfPurchase', e.target.value)}
                          className={styles.formInput}
                          placeholder="YYYY"
                          min="1900"
                          max={new Date().getFullYear()}
                        />
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor={`purchasePrice_${index}`} className={styles.formLabel}>
                          Purchase Price (₹)
                        </label>
                        <input
                          type="number"
                          id={`purchasePrice_${index}`}
                          value={item.purchasePrice || ''}
                          onChange={(e) => handleItemChange(index, 'purchasePrice', e.target.value)}
                          className={styles.formInput}
                          placeholder="Enter purchase price"
                          min="0"
                        />
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor={`warrantyExpiryDate_${index}`} className={styles.formLabel}>
                          Warranty Expiry Date
                        </label>
                        <input
                          type="date"
                          id={`warrantyExpiryDate_${index}`}
                          value={item.warrantyExpiryDate || ''}
                          onChange={(e) => handleItemChange(index, 'warrantyExpiryDate', e.target.value)}
                          className={styles.formInput}
                        />
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor={`location_${index}`} className={styles.formLabel}>
                          Storage Location
                        </label>
                        <input
                          type="text"
                          id={`location_${index}`}
                          value={item.location || ''}
                          onChange={(e) => handleItemChange(index, 'location', e.target.value)}
                          className={styles.formInput}
                          placeholder="e.g., Warehouse A - Shelf 3"
                        />
                      </div>
                      
                      <div className={styles.formGroup}>
                        <label htmlFor={`notes_${index}`} className={styles.formLabel}>
                          Maintenance Notes
                        </label>
                        <textarea
                          id={`notes_${index}`}
                          value={item.maintenanceNotes || ''}
                          onChange={(e) => handleItemChange(index, 'maintenanceNotes', e.target.value)}
                          className={styles.formTextarea}
                          placeholder="Optional notes about this specific item"
                          rows="2"
                          maxLength={500}
                        />
                      </div>
                      
                      <div className={styles.photoUploadSection}>
                        {item.photoPreview ? (
                          <div className={styles.photoPreviewContainer}>
                            <img 
                              src={item.photoPreview} 
                              alt={`Item ${index + 1}`} 
                              className={styles.photoPreview} 
                            />
                            <button 
                              type="button" 
                              className={styles.removePhotoButton}
                              onClick={() => {
                                const updatedItems = [...individualItems];
                                updatedItems[index].photoFile = null;
                                updatedItems[index].photoPreview = '';
                                setIndividualItems(updatedItems);
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        ) : (
                          <label className={styles.photoUploadArea}>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleIndividualPhotoChange(e, index)}
                              className={styles.fileInput}
                            />
                            <FontAwesomeIcon icon={faUpload} className={styles.uploadIcon} />
                            <div className={styles.uploadText}>
                              <p>Item photo (optional)</p>
                              <span className={styles.uploadInfo}>
                                Click to browse
                              </span>
                            </div>
                          </label>
                        )}
                        {formErrors[`itemPhoto_${index}`] && (
                          <p className={styles.errorText}>{formErrors[`itemPhoto_${index}`]}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Keywords section */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>
              Search Keywords
              <span className={styles.optionalTag}>(Generated automatically)</span>
            </h3>
            
            <div className={styles.keywordTags}>
              {generateKeywords().map((keyword, index) => (
                <span key={index} className={styles.keywordTag}>
                  {keyword}
                </span>
              ))}
            </div>
          </div>
          
         
          {/* Attachment Section */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Attachment (Optional)</h3>
            {attachmentName ? (
              <div className={styles.attachmentPreview}>
                <FontAwesomeIcon icon={faFilePdf} className={styles.pdfIcon} />
                <span className={styles.attachmentName}>{attachmentName}</span>
                <button 
                  type="button" 
                  className={styles.removeAttachmentButton}
                  onClick={() => {
                    setAttachmentFile(null);
                    setAttachmentName('');
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ) : (
              <label className={styles.attachmentUploadArea}>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleAttachmentChange}
                  className={styles.fileInput}
                />
                <FontAwesomeIcon icon={faFilePdf} className={styles.uploadIcon} />
                <div className={styles.uploadText}>
                  <p>Drop your PDF here or click to browse</p>
                  <span className={styles.uploadInfo}>
                    Maximum file size: 5MB
                  </span>
                </div>
              </label>
            )}
            {formErrors.attachment && (
              <p className={styles.errorText}>{formErrors.attachment}</p>
            )}
          </div>
          
          {/* Form Actions */}
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className={styles.spinnerIcon} />
                  <span>{uploadProgress ? `Uploading ${uploadProgress}%` : 'Saving...'}</span>
                </>
              ) : (
                <span>{editItem ? 'Update Equipment' : 'Add Equipment'}</span>
              )}
            </button>
          </div>
          
          {formErrors.submit && (
            <p className={styles.submitError}>{formErrors.submit}</p>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddAudioEquipmentModal;